import { Sprite, Texture } from "pixi.js";

export type WashOptions = {
  getRGBAt: (x: number, y: number, idx: number) => [number, number, number];
  alpha?: number;
};

/**
 * Create a lightweight grayscale wash: one pixel per grid cell.
 * It is scaled up in world space to cover the mosaic.
 */
export function createGrayscaleWashLayer(
  gridW: number,
  gridH: number,
  tilePx: number,
  opts: WashOptions
): Sprite {
  const { getRGBAt, alpha = 1 } = opts;

  const canvas = document.createElement("canvas");
  canvas.width = gridW;
  canvas.height = gridH;

  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) throw new Error("2D canvas not available");

  const lum = (r: number, g: number, b: number) =>
    0.2126 * r + 0.7152 * g + 0.0722 * b;

  const img = ctx.createImageData(gridW, gridH);
  const data = img.data;

  for (let y = 0; y < gridH; y++) {
    for (let x = 0; x < gridW; x++) {
      const idx = y * gridW + x;
      const [r, g, b] = getRGBAt(x, y, idx);
      const L = Math.round(lum(r, g, b));
      const o = idx * 4;
      data[o + 0] = L;
      data[o + 1] = L;
      data[o + 2] = L;
      data[o + 3] = Math.round(alpha * 255);
    }
  }
  ctx.putImageData(img, 0, 0);

  const tex = Texture.from(canvas);
  tex.source.scaleMode = "nearest"; // crisp scaling
  tex.source.autoGenerateMipmaps = false;

  const spr = new Sprite(tex);
  spr.eventMode = "none";
  // scale 1 canvas pixel → 1 world tile
  spr.scale.set(tilePx, tilePx);

  return spr;
}
